import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Pagination,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import WorklistFilters from "./WorklistFilters";
import CareGapCard from "./CareGapCard";
import CareGapDetailsInline from "./CareGapDetailsInline";
import {
  ScheduleModal,
  OutreachModal,
  SubmitModal,
  WorkflowChangeModal,
} from "../modals/CareGapActionModals";

import {
  getCareGaps,
  getCareGapsCount,
  getWorkflowStatuses,
} from "../../../../utils/services/CareGapsV2/careGapsV2Service";

const EMPTY_FILTERS = {
  careGapStatusId: 53, // por defecto: OPEN
  workflowStatusId: null,
  careGapName: null,
  cipId: null,
  patientName: null,
  dateOfBirth: null,
  pcpEmployeeId: null,
  locationId: null,
};

const PAGE_SIZE = 25;

export default function WorklistTab({ measurementYear }) {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [expandedRefresh, setExpandedRefresh] = useState(0);

  const [modal, setModal] = useState({ type: null, careGap: null });
  const [toast, setToast] = useState({ open: false, severity: "success", msg: "" });

  // Catálogo de workflow statuses (dropdown del modal "Care Gap Actions").
  // Se carga una vez al montar; alimenta el WorkflowChangeModal agrupado.
  const [workflowStatuses, setWorkflowStatuses] = useState([]);

  useEffect(() => {
    getWorkflowStatuses()
      .then((arr) => setWorkflowStatuses(Array.isArray(arr) ? arr : []))
      .catch(() => setWorkflowStatuses([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...appliedFilters,
        measurementYear: measurementYear || undefined,
        page,
        pageSize: PAGE_SIZE,
      };
      const [listRes, countRes] = await Promise.all([
        getCareGaps(params),
        getCareGapsCount(params),
      ]);
      setData(Array.isArray(listRes?.data) ? listRes.data : []);
      setTotalRecords(Number(countRes?.totalRecords ?? 0));
    } catch (e) {
      console.error("Error loading worklist:", e);
      setToast({ open: true, severity: "error", msg: "Failed to load the care gaps list." });
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, measurementYear, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / PAGE_SIZE)),
    [totalRecords]
  );

  const handleApply = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleClear = () => {
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setPage(1);
  };

  const handleAction = (type, careGap) => setModal({ type, careGap });
  const closeModal = () => setModal({ type: null, careGap: null });

  const handleSaved = (label) => {
    setToast({ open: true, severity: "success", msg: label });
    setExpandedRefresh((k) => k + 1);
    fetchData();
  };

  const handleToggleCard = (careGap) => {
    setExpandedId((current) => (current === careGap.careGapId ? null : careGap.careGapId));
  };

  const activeFiltersChips = useMemo(() => {
    const chips = [];
    if (appliedFilters.careGapStatusId === 53) chips.push("Only OPEN");
    if (appliedFilters.careGapStatusId === 54) chips.push("Only SUBMITTED");
    if (appliedFilters.careGapStatusId === 55) chips.push("Only CLOSED");
    if (appliedFilters.workflowStatusId) chips.push(`Workflow #${appliedFilters.workflowStatusId}`);
    if (appliedFilters.careGapName) chips.push(`Measure: ${appliedFilters.careGapName}`);
    if (appliedFilters.patientName) chips.push(`Name: ${appliedFilters.patientName}`);
    if (appliedFilters.cipId) chips.push(`CIP: ${appliedFilters.cipId}`);
    if (appliedFilters.dateOfBirth) chips.push(`DOB: ${appliedFilters.dateOfBirth}`);
    if (appliedFilters.pcpEmployeeId) chips.push(`PCP #${appliedFilters.pcpEmployeeId}`);
    if (appliedFilters.locationId) chips.push(`Location #${appliedFilters.locationId}`);
    return chips;
  }, [appliedFilters]);

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
        alignItems: "start",
      }}
    >
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, position: { md: "sticky" }, top: { md: 16 } }}>
        <WorklistFilters
          value={filters}
          workflowStatuses={workflowStatuses}
          onChange={setFilters}
          onApply={handleApply}
          onClear={handleClear}
        />
      </Paper>

      <Box>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="body2" fontWeight={700}>
              {loading ? "Loading…" : `${totalRecords.toLocaleString()} care gaps`}
            </Typography>
            {activeFiltersChips.map((c) => (
              <Chip key={c} size="small" label={c} variant="outlined" />
            ))}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {loading && data.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : data.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No care gaps match the current filters.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {data.map((cg) => {
              const isExpanded = expandedId === cg.careGapId;
              return (
                <Box key={cg.careGapId}>
                  <CareGapCard
                    careGap={cg}
                    expanded={isExpanded}
                    onSelect={handleToggleCard}
                    onAction={handleAction}
                  />
                  <CareGapDetailsInline
                    open={isExpanded}
                    careGap={isExpanded ? cg : null}
                    onClose={() => setExpandedId(null)}
                    onAction={handleAction}
                    refreshKey={expandedRefresh}
                  />
                </Box>
              );
            })}
          </Stack>
        )}

        {totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              shape="rounded"
              color="primary"
            />
          </Stack>
        )}
      </Box>

      <ScheduleModal
        open={modal.type === "schedule"}
        careGap={modal.careGap}
        onClose={closeModal}
        onSaved={() => handleSaved("Appointment scheduled.")}
      />
      <OutreachModal
        open={modal.type === "outreach"}
        careGap={modal.careGap}
        workflowStatuses={workflowStatuses}
        onClose={closeModal}
        onSaved={() => handleSaved("Outreach saved.")}
      />
      <SubmitModal
        open={modal.type === "submit"}
        careGap={modal.careGap}
        onClose={closeModal}
        onSaved={() => handleSaved("Availity submit saved.")}
      />
      <WorkflowChangeModal
        open={modal.type === "workflow"}
        careGap={modal.careGap}
        workflowStatuses={workflowStatuses}
        onClose={closeModal}
        onSaved={() => handleSaved("Workflow updated.")}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast({ ...toast, open: false })}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
